/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { isEmpty } from '@ember/utils';
import { registerDestructor } from '@ember/destroyable';
import { elementContainsTarget } from 'cio/utils/dom';

enum SearchType {
  NameStart = 'name-start',
}

interface Args {
  onSearch: (name: string) => void;
  name: string;
  objectTypeSingularName: string;
}

export default class ObjectListNameSearch extends Component<Args> {
  @tracked name = this.args.name ?? '';
  @tracked isOpen = false;
  @tracked focusedItem: SearchType | null = null;
  @tracked rootElement: null | HTMLInputElement = null;
  @tracked inputElement: null | HTMLInputElement = null;

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    registerDestructor(this, () => {
      document.removeEventListener('click', this.onClick);
    });
  }

  @action
  onInsert(element: HTMLInputElement) {
    this.rootElement = element;

    document.addEventListener('click', this.onClick);
  }

  @action
  onInsertInput(element: HTMLInputElement) {
    this.inputElement = element;
  }

  get searchOptions() {
    if (typeof this.name !== 'string' || isEmpty(this.name.trim())) {
      return [];
    }

    return [
      {
        id: SearchType.NameStart,
        label: `Search for ${this.args.objectTypeSingularName.toLowerCase()} names that start with <code>${
          this.name
        }</code>`,
      },
    ];
  }

  @action
  onSubmit(e: Event) {
    e.preventDefault();

    if (this.focusedItem != null) {
      this.name = `${this.name.replace(/\*/g, '')}*`;
      this.args.onSearch(this.name);
    } else {
      this.args.onSearch(this.name);
    }
  }

  @action
  clearSearch() {
    this.name = '';
    this.args.onSearch('');
    this.#close();
  }

  @action
  onFocus() {
    this.isOpen = !isEmpty(this.searchOptions);
  }

  @action
  findDropdown() {
    return this.inputElement;
  }

  @action
  onFocusItem(item: SearchType) {
    this.focusedItem = item;
  }

  @action
  onKeyUp(event: KeyboardEvent) {
    let { key } = event;

    // Enter will submit the form and close the menu, so
    // we don't want to handle that here.
    if (key === 'Enter') {
      if (this.focusedItem != null) {
        this.#close();
      }
      return;
    }

    if (['ArrowUp', 'ArrowDown'].includes(key)) {
      event.preventDefault();
      this.focusedItem = this.searchOptions[0].id;
      return;
    }

    this.isOpen = !isEmpty(this.searchOptions);

    if (!this.isOpen) {
      this.focusedItem = null;
    }

    return;
  }

  @action
  toggleSelection() {
    this.name = `${this.name.replace(/\*/g, '')}*`;
    this.args.onSearch(this.name);
    this.#close();
  }

  #close() {
    this.isOpen = false;
    this.focusedItem = null;
  }

  @action
  onClick(event: MouseEvent) {
    if (this.rootElement == null || !this.isOpen) {
      return;
    }

    let isComponent = this.rootElement === event.target;
    let inComponent = elementContainsTarget(this.rootElement, event.target);

    if (isComponent || inComponent) {
      return;
    }

    this.isOpen = false;
    this.focusedItem = null;
    return;
  }
}
